import React from 'react';
import { ChevronRight, Check, Star } from 'lucide-react';
import { useDashTheme } from '../../DashboardV2/hooks/useDashTheme';
import { Card, Badge } from '../../DashboardV2/components/primitives';
import { useGoalsSettings } from '../../../contexts/GoalsSettingsContext';
import MonthlyRoadmap from './MonthlyRoadmap';
import GoalsStatChips from './GoalsStatChips';
import GoalsHeader from './GoalsHeader';
import { goalState } from '../utils/projectProgress';

const GoalsDashboard = ({
  bannerConfig,
  months,
  onMonthClick,
  onAddGoal,
  employeeAvgScore,
  onBadgeInfoClick,
  onStreakInfoClick,
  hasReportees = false,
  currentCycle = null,
  badgeRanges = [],
  streakIconUrl = null,
  isOrgAdmin = false,
  isAutonomousMode = false,
  goalsCount = 0,
  maxGoals = 0,
  isEvaluationWindowActive = false,
  hasGoalsInCurrentCycle = false,
  isSubmissionWindowActive = false,
  scoreBenchmark = null,
  currentGoals = [],
}) => {
  const t = useDashTheme();
  const { settings: goalsSettings } = useGoalsSettings();
  const flowMode = goalsSettings?.goalsMode || 'classic';

  const areGoalsEvaluated = (() => {
    if (!currentCycle) return false;
    const isStatusLocked = currentCycle.status === 'completed' || currentCycle.status === 'locked';

    const checkWindow = (start, end) => {
      if (!start || !end) return false;
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      const startDate = new Date(start);
      const endDate = new Date(end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      return now >= startDate && now <= endDate;
    };

    const isAnyWindowActive = checkWindow(currentCycle.submissionStart, currentCycle.submissionEnd) ||
      checkWindow(currentCycle.reviewStart, currentCycle.reviewEnd) ||
      checkWindow(currentCycle.evaluationStart, currentCycle.evaluationEnd);

    // If ANY window is active by dates, they are NOT locked out yet
    if (isAnyWindowActive) return false;

    return isStatusLocked;
  })();

  // Determine current active phase
  const currentPhase = (() => {
    if (!currentCycle) return null;
    const checkWindow = (start, end) => {
      if (!start || !end) return false;
      const now = new Date();
      const s = new Date(start); s.setHours(0, 0, 0, 0);
      const e = new Date(end); e.setHours(23, 59, 59, 999);
      return now >= s && now <= e;
    };
    if (checkWindow(currentCycle.submissionStart, currentCycle.submissionEnd)) return 'submission';
    if (checkWindow(currentCycle.reviewStart, currentCycle.reviewEnd)) return 'review';
    if (checkWindow(currentCycle.evaluationStart, currentCycle.evaluationEnd)) return 'evaluation';
    return null;
  })();

  // Badge icon from settings matching the user's current rank label
  const badgeIconUrl = (() => {
    const badgeLabel = bannerConfig?.rankLabel || '';
    if (!badgeRanges || badgeRanges.length === 0) return null;
    let badgeRange = badgeRanges.find(r => r.label.toLowerCase() === badgeLabel.toLowerCase());
    if (!badgeRange && badgeLabel) {
      badgeRange = badgeRanges.find(r =>
        badgeLabel.toLowerCase().includes(r.label.toLowerCase()) ||
        r.label.toLowerCase().includes(badgeLabel.toLowerCase())
      );
    }
    if (!badgeRange) badgeRange = badgeRanges.find(r => r.icon?.url && r.icon.url.trim() !== '');
    const iconUrl = badgeRange?.icon?.url;
    return (iconUrl && typeof iconUrl === 'string' && iconUrl.trim() !== '') ? iconUrl : null;
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: t.s[4], fontFamily: t.font.family }}>
      {/* One cycle-overview card: header (cycle, windows, CTA) + stat strip */}
      <Card padding={0}>
        <div style={{ padding: `${t.s[4]}px ${t.s[4]}px ${t.s[3]}px` }}>
          <GoalsHeader
            currentCycle={currentCycle}
            bannerConfig={bannerConfig}
            employeeAvgScore={employeeAvgScore}
            months={months}
            goalsCount={goalsCount}
            maxGoals={maxGoals}
            hasGoalsInCurrentCycle={hasGoalsInCurrentCycle}
            isEvaluationWindowActive={isEvaluationWindowActive}
            areGoalsEvaluated={areGoalsEvaluated}
            isOrgAdmin={isOrgAdmin}
            isAutonomousMode={isAutonomousMode}
            onMonthClick={onMonthClick}
          />
        </div>
        <div style={{ borderTop: `1px solid ${t.c.border}`, padding: `${t.s[3]}px ${t.s[4]}px` }}>
          <GoalsStatChips
            bare
            bannerConfig={bannerConfig}
            badgeIconUrl={badgeIconUrl}
            streakIconUrl={streakIconUrl}
            employeeAvgScore={employeeAvgScore}
            goalsCount={goalsCount}
            maxGoals={maxGoals}
            onBadgeInfoClick={onBadgeInfoClick}
            onStreakInfoClick={onStreakInfoClick}
            scoreBenchmark={scoreBenchmark}
            benchmarkMaxScore={currentCycle?.scoringMode === 'star5' ? 5 : 10}
          />
        </div>

        {/* This month's goals — inline, so the current cycle is one glance away */}
        {currentGoals.length > 0 && (
          <div style={{ borderTop: `1px solid ${t.c.border}`, padding: `${t.s[3]}px ${t.s[4]}px` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: t.s[2] }}>
              <span style={{ fontSize: t.font.size.sm, fontWeight: t.font.weight.semibold, color: t.c.textSecondary }}>
                This month&apos;s goals
              </span>
              <button
                onClick={() => onMonthClick && currentCycle && onMonthClick(currentCycle.month)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 2, border: 'none', background: 'transparent',
                  cursor: 'pointer', fontSize: t.font.size.sm, color: t.c.primary, fontWeight: t.font.weight.medium,
                  padding: 0, fontFamily: t.font.family,
                }}
              >
                Open <ChevronRight size={13} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {currentGoals.slice(0, 5).map((g) => {
                const statusColor = g.status === 'APPROVED' ? t.status.positive : g.status === 'REJECTED' ? t.status.negative : t.status.warning;
                // Right-side state cell, per flow mode (shared semantics)
                const state = (() => {
                  const s = goalState(g, flowMode);
                  if (s.kind === 'done') {
                    const Icon = (flowMode === 'star' || g.scoringMode === 'star5') ? Star : Check;
                    return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: t.status.positive }}><Icon size={12} strokeWidth={2.5} /> {s.label}</span>;
                  }
                  if (s.kind === 'waiting') return <span style={{ color: t.status.warning }}>{s.label}</span>;
                  if (s.kind === 'score') return <span style={{ color: t.c.text, fontWeight: t.font.weight.semibold, fontVariantNumeric: 'tabular-nums' }}>{s.label}</span>;
                  return <span style={{ color: t.c.textTertiary }}>{s.label}</span>;
                })();
                return (
                  <div
                    key={g._id}
                    onClick={() => onMonthClick && currentCycle && onMonthClick(currentCycle.month)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: t.s[3],
                      padding: `${t.s[2]}px 0`, cursor: 'pointer',
                      borderBottom: `1px solid ${t.c.border}40`,
                    }}
                  >
                    <span title={g.status} style={{ width: 6, height: 6, borderRadius: 99, background: statusColor, flexShrink: 0 }} />
                    <span style={{
                      flex: 1, minWidth: 0, fontSize: t.font.size.base, color: t.c.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {g.name}
                    </span>
                    {g.critical && <Badge tone="negative">Critical</Badge>}
                    <span style={{ fontSize: t.font.size.xs, color: t.c.textTertiary, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {g.weightage != null ? `${g.weightage}${flowMode === 'star' || g.scoringMode === 'star5' ? ' pts' : '%'}` : ''}
                    </span>
                    <span style={{ fontSize: t.font.size.sm, whiteSpace: 'nowrap', minWidth: 90, textAlign: 'right' }}>{state}</span>
                  </div>
                );
              })}
              {currentGoals.length > 5 && (
                <button
                  onClick={() => onMonthClick && currentCycle && onMonthClick(currentCycle.month)}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
                    padding: `${t.s[2]}px 0 0`, fontSize: t.font.size.sm, color: t.c.textSecondary, fontFamily: t.font.family,
                  }}
                >
                  +{currentGoals.length - 5} more…
                </button>
              )}
            </div>
          </div>
        )}
      </Card>

      <MonthlyRoadmap
        months={months}
        onMonthClick={onMonthClick}
        streakIconUrl={streakIconUrl}
        isOrgAdmin={isOrgAdmin}
        isAutonomousMode={isAutonomousMode}
        currentPhase={currentPhase}
      />
    </div>
  );
};

export default GoalsDashboard;
